/**
 * Caliber display ordering (#109).
 *
 * The caliber facet used to render in API order — i.e. by offer count — which
 * looks random to shoppers (.357 above .300 Blackout, .380 ACP above .308).
 * These helpers sort caliber labels into a sensible ascending order by their
 * approximate bore diameter instead.
 *
 * This is a purely presentational sort. It parses the numeric magnitude out of
 * the label generically (no hard-coded caliber strings — CLAUDE.md taxonomy
 * rule) and normalizes imperial vs metric onto a single millimetre axis so that
 * same-bore cartridges cluster together.
 */

/**
 * Approximate bore diameter in millimetres parsed from a caliber label, or
 * `null` when none can be derived (shotgun gauges, unknown labels) so callers
 * can group those after the numeric calibers.
 *
 * Imperial calibers (leading dot or a value < 1, e.g. ".308", ".22 LR") are
 * converted from inches to millimetres; metric calibers ("9mm", "5.56 NATO",
 * "7.62x39") are taken as-is.
 *
 * @param {string} label - caliber label as it appears in the facet
 * @returns {number | null}
 */
export function caliberBoreMm(label: string): number | null {
  const s = label.trim().toLowerCase();
  if (!s) return null;
  // Shotgun gauges / bores aren't a diameter on the same axis — defer them.
  if (/\bgauge\b|\bga\b|\bbore\b/.test(s)) return null;
  const match = s.match(/\.?\d+(?:\.\d+)?/);
  if (!match) return null;
  const n = Number.parseFloat(match[0]);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Leading-dot or sub-1 value → imperial inches → millimetres.
  if (match[0].startsWith('.') || n < 1) return n * 25.4;
  return n; // already metric millimetres
}

/**
 * Comparator for caliber labels — ascending by bore diameter, with unknown /
 * gauge entries sorted after the numeric calibers and ties broken
 * alphabetically (e.g. "7.62x39" vs "7.62 NATO").
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function compareCalibers(a: string, b: string): number {
  const am = caliberBoreMm(a);
  const bm = caliberBoreMm(b);
  if (am === null && bm === null) return a.localeCompare(b);
  if (am === null) return 1;
  if (bm === null) return -1;
  if (am !== bm) return am - bm;
  return a.localeCompare(b);
}

/**
 * SEO-friendly slug for a caliber label (#105). Lowercases, drops the noisy
 * punctuation that makes query-string caliber URLs ugly, and collapses
 * everything else to single hyphens: ".45 ACP" → "45-acp", "9mm Luger" →
 * "9mm-luger", "5.56 NATO" → "5-56-nato", "7.62x39" → "7-62x39".
 *
 * The transform is lossy (the dot in "5.56" becomes a hyphen), so slugs are
 * resolved back to a canonical caliber by matching against the known taxonomy
 * (`findCaliberBySlug`) rather than by reversing the string.
 *
 * @param {string} caliber - canonical caliber label
 * @returns {string} URL slug
 */
export function caliberToSlug(caliber: string): string {
  return caliber
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Resolve a caliber slug back to its canonical label by slugging each known
 * caliber (canonical + any aliases) and matching. Returns the canonical label
 * or null when nothing matches.
 *
 * @param {string} slug - slug from the URL (e.g. "9mm-luger")
 * @param {ReadonlyArray<{ canonical: string; aliases?: string[] }>} terms
 * @returns {string | null}
 */
export function findCaliberBySlug(
  slug: string,
  terms: ReadonlyArray<{ canonical: string; aliases?: string[] }>
): string | null {
  const target = slug.trim().toLowerCase();
  for (const term of terms) {
    if (caliberToSlug(term.canonical) === target) return term.canonical;
    for (const alias of term.aliases ?? []) {
      if (caliberToSlug(alias) === target) return term.canonical;
    }
  }
  return null;
}
